import * as angular from "angular";
import swal from "sweetalert2";

import template from "./onboarding-mode-default.html";
import messages from "../prompts";
import { getSubmittedRequestMessage } from "../prompts/submitted-request-message";
import { isStringNullishOrEmpty } from "../../../services/string-service";
import { find } from "lodash";
import moment from "moment";

import { FORMS_REQUEST } from "../../../routes";
import onboardingRequestsModule from "./onboarding-requests";
import {
	NOMENCLATURE,
	TIME_ZONES
} from "../../../constants";

export function controller(
	api,
	$userProfile,
	loadingService,
	$sanitize,
	onboardingRequests
) {
	const ctrl = this;
	let requests;
	const noTimezoneFormCodes = [
		"ME_DMR_HARVESTER_REPORT",
		"SC_SCDNR_CHARTER_VESSEL"
	];

	ctrl.format = "MM/dd/yyyy";
	ctrl.dateOptions = {
		formatYear: "yyyy",
		startingDay: 0,
		minDate: new Date("1910-01-01"),
		datepickerMode: "year"
	};
	ctrl.popupOpen = false;
	ctrl.pendingRequests = [];
	ctrl.checkboxes = [];
	ctrl.minimumCheckboxCount = 0;

	ctrl.$onChanges = (changes) => {
		if(changes.selectedForm) {
			setupForm();
		}
	};
	ctrl.$onDestroy = () => {
		requests?.dispose();
		requests = undefined;
	};

	ctrl.setTimeZoneBox = box => {
		ctrl.timeZoneBox = box;
		ctrl.timeZoneBox.setChoices(TIME_ZONES, "ianaCode", "name", true);

		const userTimeZoneGuess = moment.tz.guess();
		const matches = TIME_ZONES.filter(sc => sc.ianaCode == userTimeZoneGuess);
		if(matches.length) {
			ctrl.timeZoneBox.setValueByChoice(matches[0].ianaCode);
			ctrl.selectedTimeZone = matches[0];
		}
	};

	ctrl.handleTimeZoneValueChanged = (modelVal) => {
		ctrl.selectedTimeZone = find(TIME_ZONES, tz => tz.ianaCode === modelVal);
	};

	ctrl.isCheckedCode = (code) => {
		return ctrl.checkboxes?.some(function(c) {
			return c.code === code && c.value === true;
		});
	};

	ctrl.validate = (submitting) => {
		const requiredFields = [
			ctrl.entityName,
			ctrl.entityCode,
			ctrl.phoneNumber
		];

		if(ctrl.collectTimeZone)
			requiredFields.push(ctrl.selectedTimeZone);

		if(ctrl.requiresDateOfBirth)
			requiredFields.push(ctrl.birthDate);

		const anyMissing = requiredFields.some(value => !(value?.toString()?.trim()));

		if(anyMissing) {
			return false;
		}

		const checkedCount = ctrl.checkboxes.filter(c => c.value === true).length;
		if(ctrl.minimumCheckboxCount > 0 && checkedCount < ctrl.minimumCheckboxCount) {
			if(submitting) {
				promptValidationError("Opt-In Required", "Please select at least one opt-in requirement.");
			}

			return false;
		}

		const requiredOptional = ctrl.checkboxes.filter(c => c.requiredField && c.value === true);
		const isMissingRequiredOptional = requiredOptional.some(v => isStringNullishOrEmpty(ctrl[v.requiredField]));

		if(isMissingRequiredOptional) {
			return false;
		}

		if(ctrl.entityCode === undefined) {
			return false;
		}

		ctrl.phoneInvalid = false;
		const phoneRegex = /^[\\+]?[(]?[0-9]{3}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{4,6}$/;
		if(submitting && !phoneRegex.test(ctrl.phoneNumber)) {
			promptValidationError("Invalid Phone Number", messages.invalid.phoneNumber);
			ctrl.phoneInvalid = submitting && true;
			return false;
		}

		if(ctrl.requiresDateOfBirth) {
			ctrl.birthdateInvalid = false;
			// eslint-disable-next-line max-len
			const dateRegex = /(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})/;
			const birthdayMoment = moment(ctrl.birthDate);
			if(submitting && (!birthdayMoment.isValid() || (!(ctrl.birthDate instanceof Date) && !dateRegex.test(ctrl.birthDate)))) {
				promptValidationError("Unrecognized Birthdate", messages.invalid.dateOfBirth);

				ctrl.birthdateInvalid = submitting && true;
				return false;
			}
		}

		return true;
	};

	ctrl.makeRequest = async() => {
		const isValid = ctrl.validate(true);
		if(isValid === false) {
			return;
		}

		const checkboxVals = ctrl.checkboxes
			.filter(c => c.value === true)
			.map(c => c.code)
			.join(",");

		const optionalValues = ctrl.checkboxes
			.filter(c => c.requiredField && c.value === true && ctrl[c.requiredField] !== undefined)
			.map(f => `(${f.requiredFieldDataCode}:${ctrl[f.requiredField]})`)
			.join(",");

		const identifiersString = optionalValues !== "" ? `;[Identifiers:${optionalValues}]` : "";

		const request = {
			Entity: `${ctrl.entityName} (${ctrl.entityCode}) [${ctrl.selectedTimeZone.ianaCode}];[Classifications:${checkboxVals}]${identifiersString}`,
			PhoneNumber: ctrl.phoneNumber,
			DateOfBirth: moment(ctrl.birthDate).isValid() ? moment(ctrl.birthDate).format() : undefined
		};
		const user = $userProfile.getUser();
		const requestSession = requests;
		await loadingService.watch(api.post(FORMS_REQUEST(ctrl.selectedForm.Id, user.databaseId), request));
		if(requestSession !== requests) {
			return;
		}

		clearModel();
		let message = messages.submitted;

		if(ctrl.agencyPhone !== undefined && ctrl.agencyPhone.length > 0) {
			message = `${message}<br/><br/>Please call ${ctrl.agencyPhone} for request status updates.`;
		}
		swal.fire({
			title: "Request Submitted",
			html: getSubmittedRequestMessage(ctrl.selectedForm, message, $sanitize),
			showCloseButton: true,
			type: "success",
			showCancelButton: false,
			confirmButtonColor: "#8CC541",
			confirmButtonText: "Okay"
		});

		await loadingService.watch(requestSession.refresh(true)).catch(() => undefined);
	};

	ctrl.openDatepicker = () => {
		ctrl.popupOpen = !ctrl.popupOpen;
	};

	function setupForm() {
		requests?.dispose();
		requests = undefined;
		ctrl.pendingRequests = [];
		if(!ctrl.selectedForm) {
			return;
		}

		ctrl.entityName = undefined;
		ctrl.entityCode = undefined;
		ctrl.phoneNumber = undefined;
		ctrl.birthDate = undefined;
		ctrl.hmsPermitNumber = undefined;
		ctrl.pendingRequests = [];
		ctrl.phoneInvalid = false;
		ctrl.birthdateInvalid = false;

		ctrl.collectTimeZone = noTimezoneFormCodes.indexOf(ctrl.selectedForm?.Code) === -1;
		ctrl.entityNomenclature = find(ctrl.selectedForm?.Nomenclature, n => n.Code === "FORM.PRIMARY.ENTITY")?.Text || "Entity";
		ctrl.entityDescriptor = "Reg/CG #";
		ctrl.checkboxes = [];
		ctrl.minimumCheckboxCount = 0;
		ctrl.requiresDateOfBirth = true;

		switch(ctrl.selectedForm?.Code) {
			case "FL_FWC_TRIPTICKET_DLR":
				ctrl.requiresDateOfBirth = false;
				ctrl.entityDescriptor = "Dealer Number";
				break;

			case "ME_DMR_HARVESTER_REPORT":
				ctrl.entityDescriptor = "Landings Number";
				break;

			case "/ww/bfd/private-angler":
				ctrl.checkboxes.push(
					{
						label: "The Billfish Foundation Tagging Program",
						code: "TBF",
						value: true
					},
					{
						label: "Atlantic HMS Recreational Reporting Requirements",
						code: "HMS",
						value: true,
						requiredField: "hmsPermitNumber",
						requiredFieldDataCode: "HMS:VESSEL:PERMIT"
					});
				ctrl.minimumCheckboxCount = 1;
				break;

			case "SERO_FORHIRE":
				ctrl.checkboxes.push(
					{
						label: "The Billfish Foundation Tagging Program",
						code: "TBF",
						value: false
					});
				break;

			default:
				break;
		}

		ctrl.agencyPhone = ctrl.selectedForm.Nomenclature?.find(n => n.Code === NOMENCLATURE.SupportNumber)?.Text;

		requests = onboardingRequests.create(ctrl.selectedForm, userRequests => {
			ctrl.pendingRequests = userRequests;
		});
		loadingService.watch(requests.refresh()).catch(() => undefined);
	}

	function clearModel() {
		ctrl.entityName = undefined;
		ctrl.entityCode = undefined;
		ctrl.phoneNumber = undefined;
		ctrl.birthDate = undefined;
	}

	function promptValidationError(title, message) {
		swal.fire({
			title: title,
			html: message,
			showCloseButton: true,
			type: "warning",
			showCancelButton: false,
			confirmButtonColor: "#8CC541",
			confirmButtonText: "Okay"
		});
	}
}

export default angular
	.module("vesl.screens.welcome.onboarding-mode-default", [onboardingRequestsModule])
	.component("onboardingModeDefault", {
		bindings: {
			selectedForm: "<"
		},
		controller,
		template
	}).name;
